import React, { useState } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

function CarbamazepineCalculator({ user }) {
   // Step 1: State for Patient Type
    const [patientType, setPatientType] = useState('monotherapy'); // 'monotherapy', 'polytherapy', or 'pediatric'
    const [weight, setWeight] = useState('');
    const [dose, setDose] = useState('');
    const [tau, setTau] = useState('');
    const [bloodLevel, setBloodLevel] = useState('');
    const [simDose, setSimDose] = useState('');
    
    // Step 2: Determine CL Constant
    const getCLConstant = () => {
        if (patientType === 'monotherapy') return 0.064;
        if (patientType === 'polytherapy') return 0.10;
        if (patientType === 'pediatric') return 0.11;
        return 0.064;
    };

    const clConstant = getCLConstant();
    

    // Step 3: Estimate Css from current dose
    const S = 1.0; // Tablet
    const F = 0.85; // Bioavailability for Carbamazepine is usually around 0.7-0.8
    const weightNum = parseFloat(weight) || 0;
    const currentDose = parseFloat(dose) || 0;
    const interval = parseFloat(tau) || 0; // Default 12 hourly

    // Css = (Dose * S * F) / (CL * Interval)
    const estimatedCss = interval >0 ? (currentDose * S * F) / (clConstant * weight * interval):0;


    const deviation = estimatedCss > 0 ? ((bloodLevel - estimatedCss) / estimatedCss) * 100 : 0;
    let comparisonStatus = {
        text: "Within Range",
        color: "#2ecc71",
        note: "Patient is responding as expected for an average adult/pediatric profile."
    };

    if (bloodLevel < estimatedCss * 0.8) {
        comparisonStatus = {
            text: "Below Expectation",
            color: "#3498db",
            note: `Actual level is ${Math.abs(deviation).toFixed(0)}% lower than predicted. Check for non-compliance, malabsorption, or early sampling.`
        };
    } else if (bloodLevel > estimatedCss * 1.2) {
        comparisonStatus = {
            text: "Above Expectation",
            color: "#e67e22",
            note: `Actual level is ${deviation.toFixed(0)}% higher than predicted. Check for renal/hepatic impairment or enzyme inhibition.`
        };
    }    
    
   // === RE-ENGINEERED DOSE SIMULATION CORE (INDIVIDUAL HIGH-PRECISION) ===
    const simDoseNum = Number(simDose);
    const currentDoseNum = parseFloat(dose) || 0;
    const totalCurrentDailyDose = currentDoseNum / (tau/24)
    const bloodLevelNum = parseFloat(bloodLevel) || 0;
    
    let simulatedResult = 0;

    // If a lab result is available, use the high-precision individual patient ratio
    if (simDoseNum > 0 && currentDoseNum > 0 && bloodLevelNum > 0) {
        simulatedResult = (simDoseNum / totalCurrentDailyDose) * bloodLevelNum;
    } else if (simDoseNum > 0 && interval > 0 && weightNum > 0) {
        // Fallback fallback to population modeling ONLY if no lab level exists yet
        simulatedResult = (simDoseNum * S * F) / (clConstant * weightNum * interval);
    }

    // Logic for Step 3
    const estCssNum = Number(estimatedCss || 0);
    let cbzSuggestion = "";
    let cbzColor = "#2ecc71";

    if (bloodLevelNum >= 4 && bloodLevelNum <= 12) {
        cbzSuggestion = "Level is within therapeutic range (4-12 mg/L). If response is poor but compliance good, consider a small dose increment (do not exceed max). If response is good, continue current dose.";
        cbzColor = "#2ecc71"; // Green
    } else if (bloodLevelNum < 4) {
        cbzSuggestion = "Level is sub-therapeutic (< 4 mg/L). If response is poor, Check compliance first before consider a dose increment. If response is poor but compliance is good, increase dose and re-sample in 5 days. If response is good, continue current dose.";
        cbzColor = "#f1c40f"; // Yellow
    } else if (bloodLevelNum > 12) {
        cbzSuggestion = "Level is TOXIC (> 12 mg/L). Withhold treatment. Monitor for diplopia, hyponatremia, seizure, Arrythmia or dizziness. Re-adjust dose once symptoms/levels improve.";
        cbzColor = "#e74c3c"; // Red
    }

    // Add this logic before the return
    const getClinicalInsight = () => {
        if (!bloodLevel || !estimatedCss) return null;
        
        const actual = Number(bloodLevel);
        const estimate = Number(estimatedCss);
        
        if (actual > estimate * 1.2) { // More than 20% higher than estimate
            return {
                title: "Actual > Population Estimate",
                reason: "Patient may be a 'Slow Metabolizer' or taking an inhibitor (e.g.,Clarithromycin, Erythromycin, Diltiazem, Fluoxetine, Grapefruit juice, Isoniazid, Loratadine, Valproate, Verapamil, Azole antifungals:Fluconazole, Itraconazole, Ketoconazole, Protease inhibitor: Indinavir, Nelfinavir, Ritonavir).",
                color: "#e67e22"
            };
        } else if (actual < estimate * 0.8) { // More than 20% lower than estimate
            return {
                title: "Actual < Population Estimate",
                reason: "Patient may be a 'Fast Metabolizer', have poor absorption, or a compliance issue. Also consider induction by other drugs (e.g.,Cisplatin, Doxorubicin, Phenobarbital, Phenytoin, Primidone, Rifampicin, Theophylline, Carbamazepine (autoinduction) ).",
                color: "#3498db"
            };
        }
        return {
            title: "Matches Population Estimate",
            reason: "Patient's metabolism is consistent with standard population parameters.",
            color: "#2ecc71"
        };
    };

    const insight = getClinicalInsight();
    return (
        
        <div style={{ padding: '20px', background: '#1e272e', color: 'white', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                <button 
                    type="button"
                    className="no-print" 
                    onClick={() => {
                                        const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                                        const trackingLabel = `${actualUserEmail}-printed-Carbamazepine`;
                                        logUserActivityQuietly(trackingLabel); 
                                        window.print();
                                    }}
                    style={{
                    padding: '10px 20px',
                    backgroundColor: '#27ae60', 
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                    }}
                >
                    🖨️ Print PDF Report
                </button>
            </div>
            <h2 style ={{textAlign: 'center', color: '#3498db'}}>Carbamazepine TDM Worksheet</h2>
            <details style={{
                backgroundColor: '#1E293B',
                border: '1px solid #38BDF8',
                borderRadius: '8px',
                padding: '10px 15px',
                marginBottom: '20px',
                cursor: 'pointer'
            }}>
                <summary style={{ color: '#38BDF8', fontWeight: 'bold' }}>
                ⏱️ Check Clinical Sampling Protocol (Click to expand)
                </summary>
                <div style={{ marginTop: '12px', padding: '15px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', border: '1px solid #3d4e5d', fontSize: '0.9rem', color: '#F8FAFC' }}>
    
                    {/* SECTION 1: SAMPLING TIMELINE MATRIX TABLE */}
                    <div style={{ marginBottom: '15px' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>
                            ⏱️ Stabilization & Auto-Induction Timelines
                        </span>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                            <tbody>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff', width: '35%' }}>Therapy Initiation:</td>
                                    <td style={{ padding: '8px 0', color: '#bdc3c7' }}>
                                        Draw steady-state levels after **2 – 3 weeks** (Allows full hepatic auto-induction to complete).
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff' }}>Dose Adjustment:</td>
                                    <td style={{ padding: '8px 0', color: '#bdc3c7' }}>
                                        Draw new steady-state levels **2 – 5 days** following a single change in maintenance regimen.
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff' }}>Pre-Sample Draw:</td>
                                    <td style={{ padding: '8px 0', color: '#bdc3c7' }}>Draw a true trough sample **just before / immediately preceding** the next scheduled dose.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* SECTION 2: TARGET REFERENCE WINDOW */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '15px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        {/* Therapeutic Range Card */}
                        <div style={{ background: 'rgba(46, 204, 113, 0.06)', border: '1px solid rgba(46, 204, 113, 0.2)', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontWeight: 'bold', color: '#2ecc71', fontSize: '0.8rem', textTransform: 'uppercase' }}>🎯 Standard Therapeutic Range:</div>
                            <div style={{ fontSize: '1.4rem', fontWeight: 'bold', marginTop: '4px', color: '#fff' }}>
                                4 - 12 <span style={{ fontSize: '0.85rem', fontWeight: 'normal', color: '#bdc3c7' }}>mg/L</span>
                            </div>
                        </div>
                    </div>

                    {/* SECTION 3: SEVERE TOXICITY CRITICAL ALERTS */}
                    <div style={{ background: 'rgba(231, 76, 60, 0.08)', borderLeft: '4px solid #e74c3c', padding: '10px', borderRadius: '0 6px 6px 0' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#e74c3c', textTransform: 'uppercase', display: 'block' }}>
                            ⚠️ Severe Toxicity Alert Threshold
                        </span>
                        <div style={{ fontSize: '0.85rem', color: '#ecf0f1', marginTop: '4px', lineHeight: '1.4' }}>
                            • Standard neurotoxic side effects (ataxia, blurred vision, dizziness) can appear at upper target margins.<br />
                            • <strong style={{ color: '#ff7675' }}>CRITICAL LIFE-THREATENING TOXICITY</strong> correlates strongly with serum trough concentrations <strong>&gt; 40 mg/L</strong> (characterized by severe cardiac conduction delays, respiratory depression, and comas).
                        </div>
                    </div>

                </div>
            </details>
            {/* STEP 1: PATIENT TYPE SELECTION */}
            <section style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#3498db' }}>Step 1: Determine Clearance (CL)</h3>
                <label>Select Therapy Type:</label>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    {['monotherapy', 'polytherapy', 'pediatric'].map(type => (
                        <button 
                            key={type}
                            onClick={() => setPatientType(type)}
                            style={{
                                padding: '10px',
                                flex: 1,
                                background: patientType === type ? '#3498db' : '#34495e',
                                border: 'none', color: 'white', borderRadius: '5px', cursor: 'pointer',
                                textTransform: 'capitalize'
                            }}
                        >
                            {type}
                        </button>
                    ))}
                </div>
                <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#bdc3c7' }}>
                    CL Constant: <strong>{clConstant} L/kg/hr</strong>
                </p>
            </section>

            {/* STEP 2: PREDICT Css */}
            <section>
                <h3 style={{ color: '#3498db' }}>Step 2: Estimate Predicted Cₛₛ</h3>
                <div style={{ display: 'grid', gap: '10px' }}>
                    <div>
                        <label>Weight (kg):</label>
                        <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                        <label>Dose (mg):</label>
                        <input type="number" value={dose} onChange={(e) => setDose(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                        <label>Interval (hours):</label>
                        <input type="number" value={tau} onChange={(e) => setTau(e.target.value)} style={inputStyle} />
                    </div>
                </div>

                <div style={{ marginTop: '20px', padding: '15px', background: '#2c3e50', borderRadius: '8px', borderLeft: '5px solid #f1c40f' }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7' }}>Estimated Steady State (Cₛₛ):</p>
                    <h1 style={{ margin: '5px 0', color: '#f1c40f' }}>
                        {Number(estimatedCss || 0).toFixed(2)} <span style={{ fontSize: '1rem' }}>mg/L</span>
                    </h1>

                    {/* Transparency Formula */}
                    <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '10px', fontFamily: 'monospace' }}>
                        Formula: ({currentDose} × {S} × {F}) / ({clConstant} × {weightNum} × {interval})
                    </div>
                </div>
            </section>
            <section style={{ marginTop: '30px', padding: '20px', background: '#2c3e50', borderRadius: '10px', border: `2px solid ${cbzColor}` }}>
                <h3 style={{ color: cbzColor, margin: '0 0 15px 0' }}>Step 3: Clinical Recommendation</h3>
                
                <label>Lab Measured Level (mg/L):</label>
                <input 
                    type="number" 
                    value={bloodLevel} 
                    onChange={(e) => setBloodLevel(e.target.value)} 
                    style={{ ...inputStyle, border: `1px solid ${cbzColor}`, marginBottom: '15px' }} 
                />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                    <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '5px' }}>
                        <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>Population Estimate:</span>
                        <div style={{ fontWeight: 'bold' }}>{estCssNum.toFixed(1)} mg/L</div>
                    </div>
                    <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '5px' }}>
                        <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>Measured Level:</span>
                        <div style={{ fontWeight: 'bold', color: cbzColor }}>{bloodLevelNum.toFixed(1)} mg/L</div>
                    </div>
                </div>
                {bloodLevel > 0 && (
                    
                    <div style={{ padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', borderLeft: `6px solid ${comparisonStatus.color}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span style={{ fontWeight: 'bold', color: comparisonStatus.color }}>{comparisonStatus.text}</span>
                            <span style={{ fontSize: '0.8rem', color: '#95a5a6'}}>Dev: {deviation > 0 ? '+' : ''}{deviation.toFixed(1)}%</span>
                        </div>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7', lineHeight: '1.4' }}>
                            {comparisonStatus.note}
                        </p>
                    </div>
                )}
            </section>
            {/* STEP 4: DOSE SIMULATION (INDIVIDUAL HIGH-PRECISION PATIENT MODEL) */}
            <h4 style={{ margin: '0 0 10px 0', color: '#3498db', fontSize: '0.9rem', marginTop: '20px'}}>
                Step 4: Dose Simulation
            </h4>
            
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.7rem', color: '#95a5a6', display: 'block', marginBottom: '4px' }}>NEW PROPOSED DOSE/DAILY (mg)</label>
                    <input 
                        type="number" 
                        value={simDose}
                        onChange={(e) => setSimDose(e.target.value)}
                        placeholder="e.g. 400"
                        style={{ width: '100%', padding: '8px', background: '#1b1f23', border: simDose ? '1px solid #3498db' : '1px solid #34495e', color: 'white', borderRadius: '4px', boxSizing: 'border-box' }}
                        disabled={!bloodLevel || !dose}
                    />
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>EST. NEW CSS</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#00d2ff' }}>
                        {simulatedResult ? simulatedResult.toFixed(2) : '--'} <span style={{fontSize: '0.7rem', fontWeight: 'normal', color: '#95a5a6' }}>mg/L</span>
                    </div>
                </div>
            </div>

            {/* VISUAL SPECTRUM PROGRESS SLIDER TRACK BAR */}
            {simulatedResult > 0 && (
                <div style={{ padding: '15px', background: '#111416', borderRadius: '8px', border: '1px solid #2c3539', marginBottom: '15px' }}>
                    <div style={{ position: 'relative', height: '14px', background: '#2c3e50', borderRadius: '7px', margin: '15px 0 15px 0', overflow: 'visible' }}>
                        
                        {/* TARGET THERAPEUTIC REGION SHADOW BOUNDARY HIGHLIGHT (4.0 to 12.0 mg/L Highlight Track Zone) */}
                        {/* Maps beautifully across a 0 to 20 mg/L scale context landscape */}
                        <div style={{ 
                            position: 'absolute', 
                            left: '20%',  /* 4 mg/L is 20% of 20 mg/L max scale */
                            width: '40%', /* 12 mg/L - 4 mg/L = 8 mg/L wide span, which is 40% width */
                            height: '100%', 
                            background: 'rgba(46, 204, 113, 0.22)', 
                            borderLeft: '1px dashed #2ecc71', 
                            borderRight: '1px dashed #2ecc71',
                            borderRadius: '4px'
                        }} />

                        {/* ACTIVE PATIENT PINPOINT INDICATOR NODE (Glowing yellow indicator bead node) */}
                        <div style={{
                            position: 'absolute',
                            left: `${Math.max(0, Math.min(98, (simulatedResult / 20) * 100))}%`,
                            transform: 'translateX(-50%)',
                            width: '18px',
                            height: '18px',
                            top: '-2px',
                            background: '#f1c40f',
                            borderRadius: '50%',
                            boxShadow: '0 0 12px #f1c40f',
                            border: '2px solid #fff',
                            transition: 'left 0.2s ease-out',
                            zIndex: 10
                        }} />
                    </div>

                    {/* GRAPH SCALING X-AXIS SCALE FOOTER LABELS */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#7f8c8d', padding: '0 2px', marginBottom: '12px' }}>
                        <span>0 mg/L</span>
                        <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>Target Spec Window: 4.0 - 12.0 mg/L</span>
                        <span>20 mg/L</span>
                    </div>

                    {/* CLEAR CALCULATION AUDIT VERIFICATION LABELS */}
                    <div style={{ fontSize: '0.68rem', color: '#57606f', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '8px', lineHeight: '1.4', textAlign: 'left' }}>
                        {bloodLevelNum > 0 ? (
                            <>
                                Formula Predict Css: (Proposed New Daily Dose / Current Daily Dose) × Actual Measured Lab Level<br />
                                Individual Patient Linear Ratio Trace: ({simDose} mg / {totalCurrentDailyDose} mg) × {bloodLevel} mg/L = {simulatedResult.toFixed(2)} mg/L
                            </>
                        ) : (
                            <>
                                Formula Predict Css: (Dose × S × F) / (CL Constant × Weight × Interval)<br />
                                Population Estimate Reference Trace: ({simDose}mg × 1.0 × 0.85) / ({clConstant} × {weight}kg × {interval}hr)
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* CLINICAL RECOMMENDATION CARD PANEL */}
            <div style={{ padding: '15px', background: '#1e272e', borderRadius: '5px', borderLeft: `5px solid ${cbzColor}`, marginBottom: '15px' }}>
                <strong style={{ color: cbzColor, fontSize: '0.85rem', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Recommendation:</strong>
                <p style={{ margin: '0', lineHeight: '1.4', fontSize: '0.88rem', color: '#ecf0f1' }}>{cbzSuggestion}</p>
            </div>

            {insight && (
                <div style={{ 
                    marginTop: '15px', 
                    padding: '12px', 
                    background: '#1b1f23', 
                    borderRadius: '8px', 
                    borderLeft: `5px solid ${insight.color}`,
                    marginBottom: '15px'
                }}>
                    <h5 style={{ margin: '0 0 5px 0', color: insight.color, fontSize: '0.9rem' }}>
                        🔍 Interpretation: {insight.title}
                    </h5>
                    <p style={{ margin: '0', fontSize: '0.85rem', color: '#bdc3c7', lineHeight: '1.4' }}>
                        {insight.reason}
                    </p>
                </div>
            )}

            {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
            <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
                *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
            </p>
        </div>
    );
}
const inputStyle = { width: '50%', padding: '8px', background: '#ede181', border: '1px solid #34495e', color: '#34495e', borderRadius: '4px' };

export default CarbamazepineCalculator;